/**
 * Card / Panel — the standard container for grouped content.
 *
 * Composed pattern (similar to shadcn): consumers build a card from
 * <Card><CardHeader><CardTitle/></CardHeader><CardContent/></Card> so we
 * keep header/body padding consistent across the app.
 */
import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-2xl border border-line bg-surface overflow-hidden",
        // Subtle elevated panel shadow that adapts to theme via the var.
        "shadow-[0_1px_0_rgba(11,15,35,0.03),0_18px_50px_-28px_rgba(125,108,255,0.25)]",
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center justify-between gap-4 px-5 py-4 border-b border-line",
        className,
      )}
      {...props}
    />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("m-0 text-sm font-bold tracking-tight text-fg", className)}
      {...props}
    >
      {children}
    </h3>
  ),
);
CardTitle.displayName = "CardTitle";

export const CardSub = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("m-0 mt-0.5 text-xs text-fg-mute", className)}
      {...props}
    />
  ),
);
CardSub.displayName = "CardSub";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-5", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";
